import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { History, Loader2, MessageSquarePlus, Minus, Paperclip, SendHorizontal, Sparkles } from 'lucide-react'
import { ASSISTANT_ATTACHMENT_ACCEPT } from '@repo/types'
import { useAuth } from '@/contexts/AuthContext'
import { canUseAssistant } from './assistant-roles'
import { AiConsentGate } from './AiConsentGate'
import { useAssistant } from './AssistantContext'
import { AssistantHistory } from './AssistantHistory'
import { BubbleAttachments, ComposerAttachments } from './AssistantAttachments'
import { EmailConfirmCard } from './EmailConfirmCard'
import { MarkdownText } from './MarkdownText'
import { useAssistantAttachments } from './useAssistantAttachments'

/** Vzdialenosť od spodku (px), do ktorej sa nový obsah ešte automaticky doroluje. */
const STICK_THRESHOLD = 80

export function AssistantPanel() {
  const { user } = useAuth()
  const a = useAssistant()
  const { setOpen } = a
  const [view, setView] = useState<'chat' | 'history'>('chat')
  const [draft, setDraft] = useState('')
  const asideRef = useRef<HTMLElement>(null)
  const fabRef = useRef<HTMLButtonElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const attachments = useAssistantAttachments()
  /** Používateľ je pri spodku zoznamu → nový obsah doroluj; ak číta staršie správy, nechaj ho. */
  const stickRef = useRef(true)
  // Počas odpovede je zablokovaná len nová správa; Nový chat a História sa od nej len odpoja (server ju dokončí).
  const busy = a.isStreaming || a.isLoadingChat

  const close = useCallback(() => setOpen(false), [setOpen])

  // Vlastný Esc (nie useEscapeKey): otvorený modál (napr. potvrdenie zmazania) má prednosť.
  useEffect(() => {
    if (!a.open) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (document.querySelector('[aria-modal="true"]')) return
      close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [a.open, close])

  const scrollToBottom = () => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }
  const onScroll = () => {
    const el = scrollRef.current
    if (el) stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_THRESHOLD
  }

  // Otvorenie panelu / návrat z histórie / načítaný chat → vždy na koniec.
  useLayoutEffect(() => {
    if (!a.open || view !== 'chat') return
    stickRef.current = true
    scrollToBottom()
  }, [a.open, view, a.isLoadingChat])
  // Nový obsah → doroluj, len ak je používateľ pri spodku (alebo práve sám odoslal).
  useLayoutEffect(() => {
    if (stickRef.current) scrollToBottom()
  }, [a.bubbles, a.activity, a.pendingEmail, a.isStreaming])

  // Fokus do inputu po otvorení a po dobehnutí odpovede (textarea je počas nej disabled) —
  // ale nekradni ho, ak medzitým používateľ pracuje inde na stránke.
  useEffect(() => {
    if (!a.open || view !== 'chat' || busy) return
    const active = document.activeElement
    if (!active || active === document.body || asideRef.current?.contains(active)) inputRef.current?.focus()
  }, [a.open, view, busy])

  // Po zatvorení vráť fokus na FAB (ak bol vo vnútri panelu, inak by ostal na skrytom prvku).
  const wasOpenRef = useRef(a.open)
  useEffect(() => {
    if (wasOpenRef.current && !a.open) {
      const active = document.activeElement
      if (!active || active === document.body || asideRef.current?.contains(active)) fabRef.current?.focus()
    }
    wasOpenRef.current = a.open
  }, [a.open])

  if (!canUseAssistant(user)) return null

  const canSend = (!!draft.trim() || attachments.ready.length > 0) && !attachments.uploading && !busy
  const submit = () => {
    if (!canSend) return
    stickRef.current = true
    a.send(draft, attachments.ready, setDraft)
    setDraft('')
    attachments.clear()
  }
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      submit()
    }
  }

  return (
    <>
      {!a.open && (
        <button
          ref={fabRef}
          type="button"
          aria-label="Otvoriť AI asistenta"
          title="AI asistent"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
        >
          <Sparkles className="h-6 w-6" />
        </button>
      )}

      {/* invisible (visibility) po dobehnutí animácie vyradí zatvorený panel z tab poradia. */}
      <aside
        ref={asideRef}
        aria-label="AI asistent"
        aria-hidden={!a.open}
        className={`fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l border-gray-200 bg-white shadow-2xl transition-[transform,visibility] duration-200 ease-out sm:w-[420px] dark:border-gray-700 dark:bg-gray-800 ${a.open ? 'visible translate-x-0' : 'pointer-events-none invisible translate-x-full'}`}
      >
        <header className="flex items-center gap-1 border-b border-gray-200 px-3 py-2 dark:border-gray-700">
          <Sparkles className="h-5 w-5 text-brand-600 dark:text-brand-400" />
          <h2 className="flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100">AI asistent</h2>
          <button type="button" className="btn-ghost p-2" title="Nový chat" aria-label="Nový chat" disabled={a.isLoadingChat || !user?.aiConsentAt} onClick={() => { a.reset(); setView('chat') }}>
            <MessageSquarePlus className="h-4 w-4" />
          </button>
          <button
            type="button"
            className={`btn-ghost p-2 ${view === 'history' ? 'bg-gray-100 dark:bg-gray-700' : ''}`}
            title="História"
            aria-label="História"
            aria-pressed={view === 'history'}
            disabled={a.isLoadingChat || !user?.aiConsentAt}
            onClick={() => setView(view === 'history' ? 'chat' : 'history')}
          >
            <History className="h-4 w-4" />
          </button>
          {/* Panel sa len skryje, chat ostáva rozpísaný. */}
          <button type="button" className="btn-ghost p-2" title="Minimalizovať" aria-label="Minimalizovať" onClick={close}>
            <Minus className="h-4 w-4" />
          </button>
        </header>

        {!user?.aiConsentAt ? (
          <AiConsentGate onDecline={() => setOpen(false)} />
        ) : view === 'history' ? (
          <AssistantHistory
            activeChatId={a.chatId}
            disabled={a.isLoadingChat}
            onOpen={(id) => { void a.loadChat(id); setView('chat') }}
            onDeleted={(id) => { if (id === a.chatId) a.reset() }}
          />
        ) : (
          <>
            <div ref={scrollRef} onScroll={onScroll} className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
              {a.isLoadingChat ? (
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Načítavam konverzáciu…
                </div>
              ) : (
                <>
                  {a.bubbles.length === 0 && !a.pendingEmail && !a.isStreaming && (
                    <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
                      Opýtajte sa napríklad: „Ktoré moje obchody sú po lehote?“
                    </p>
                  )}
                  {a.bubbles.map((b) => (
                    <div key={b.id} className={b.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                      <div
                        className={`max-w-[85%] break-words rounded-2xl px-3 py-2 ${
                          b.role === 'user'
                            ? 'whitespace-pre-wrap bg-brand-600 text-sm text-white'
                            : b.isError
                              ? 'whitespace-pre-wrap bg-red-50 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300'
                              : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                        }`}
                      >
                        {b.attachments && <BubbleAttachments attachments={b.attachments} />}
                        {b.role === 'assistant' && !b.isError ? <MarkdownText text={b.text} /> : b.text}
                      </div>
                    </div>
                  ))}
                  {a.pendingEmail && <EmailConfirmCard pending={a.pendingEmail} onRespond={a.respondToEmail} disabled={busy} />}
                  {a.isStreaming && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      {a.activity ?? 'Premýšľam…'}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="border-t border-gray-200 p-3 dark:border-gray-700">
              <ComposerAttachments items={attachments.items} onRemove={attachments.remove} />
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={ASSISTANT_ATTACHMENT_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) attachments.add(e.target.files)
                    e.target.value = ''
                  }}
                />
                <button
                  type="button"
                  className="btn-ghost p-2.5"
                  aria-label="Priložiť súbor"
                  title="Priložiť obrázok alebo dokument (PDF, Word, Excel, PowerPoint)"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Paperclip className="h-4 w-4" />
                </button>
                <textarea
                  ref={inputRef}
                  rows={2}
                  value={draft}
                  disabled={busy}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Napíšte správu…"
                  className="input max-h-40 min-h-[2.5rem] flex-1 resize-none"
                />
                <button type="button" className="btn-primary p-2.5" aria-label="Odoslať" title="Odoslať" disabled={!canSend} onClick={submit}>
                  <SendHorizontal className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>
  )
}
